// order_tracking/index.jsx — Template RX `order_tracking` (CONTRAT FIGÉ A3)
// ─────────────────────────────────────────────────────────────────────────────
// Rendu PUR du payload backend (déjà validé Zod des 2 côtés). Zéro logique métier.
// Carte : Mapbox Static Images API (position livreur, rafraîchie à chaque poll) —
// zéro dépendance WebGL dans le fil de discussion.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import './order_tracking.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';
const TERMINAL = ['completed', 'delivered', 'dispatch_expired', 'disputed', 'cancelled'];

function mapUrl(pos) {
  if (!MAPBOX_TOKEN || !pos) return null;
  const { lat, lng } = pos;
  return `https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/pin-l+FF7E1B(${lng},${lat})/${lng},${lat},13.2/560x220@2x?access_token=${MAPBOX_TOKEN}&attribution=false&logo=false`;
}

export function OrderTrackingTemplate({ payload, onAction }) {
  if (!payload || !Array.isArray(payload.sections)) return null;
  const status = payload.sections.find((s) => s.type === 'tracking_status');
  const map = payload.sections.find((s) => s.type === 'tracking_map');
  const timeline = payload.sections.find((s) => s.type === 'timeline');
  if (!status) return null;
  const terminal = TERMINAL.includes(status.state);
  const url = map ? mapUrl(map.courier_position) : null;

  return (
    <div className="ot-card" data-testid="rx-order-tracking">
      <div className="ot-header">
        <span className="ot-title">{payload.header_text}</span>
        <span className={`ot-state${terminal ? ' ot-terminal' : ''}`} data-testid="ot-state">{status.state_label}</span>
      </div>

      {url && (
        <img
          className="ot-map"
          src={url}
          alt={`Position du livreur${status.resource ? ` ${status.resource.first_name}` : ''}`}
          data-testid="ot-map"
        />
      )}

      <div className="ot-row">
        <span className="ot-resource">
          {status.resource ? <>🛵 <strong>{status.resource.first_name}</strong></> : '🔎 Recherche du livreur…'}
          {' · '}{status.merchant.name}{status.merchant.location ? ` (${status.merchant.location})` : ''}
        </span>
        {status.eta_min ? <span className="ot-eta" data-testid="ot-eta">⏱ ~{status.eta_min} min</span> : null}
      </div>

      {status.pin && (
        <div className="ot-pin" data-testid="ot-pin">
          <span aria-hidden="true">🔐</span>
          <span className="ot-pin-code">{status.pin}</span>
          <span className="ot-pin-hint">Code de réception — donne-le au livreur à l’arrivée</span>
        </div>
      )}

      <div className="ot-dest">
        📍 Livraison : {status.drop.label || 'ton adresse'}{status.drop.code ? <> · <code>{status.drop.code}</code></> : null}
        {' · '}{new Intl.NumberFormat('fr-FR').format(status.amount_fcfa)} FCFA
      </div>

      {timeline && (
        <ul className="ot-timeline" data-testid="ot-timeline">
          {timeline.steps.map((s) => (
            <li key={s.id}>
              {s.label}
              {s.ts ? <time>{s.ts.slice(11, 16)}</time> : null}
            </li>
          ))}
        </ul>
      )}

      {!terminal && Array.isArray(payload.actions) && payload.actions.length > 0 && (
        <div className="ot-actions">
          {payload.actions.map((a) => (
            <button
              key={a.action_id}
              type="button"
              data-testid={`ot-action-${a.action_id}`}
              onClick={() => onAction && onAction(a.action_id, { order_id: status.order_id })}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default OrderTrackingTemplate;

export const TEMPLATE_META = {
  template: 'order_tracking',
  layout: 'card',
  priority: 'high',
  version: 'rx.v1',
  spec_ref: 'CONTRACT_order_tracking_v1.md',
  description: 'Suivi de livraison temps réel — alimenté par le Resource Dispatch Engine.',
};
